import React from 'react';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  hasRolePermission,
  isRoleModuleEnabled,
  MASTER_MODULE_LABELS,
  MATRIX_MODULE_KEYS,
  MATRIX_MODULE_LABELS,
  PERMISSION_ROLE_LABELS,
  toggleRoleModuleEnabled,
  toggleRolePermission,
} from '../../lib/rolePermissions';

/**
 * Granular module permission matrix for selected roles.
 * @param {'simple' | 'detailed'} [variant] — simple ON/OFF toggles for core modules
 */
export default function RolePermissionsMatrix({
  rolePermissions,
  onChange,
  editableRoles,
  activeModuleKeys,
  disabled = false,
  variant = 'simple',
}) {
  const moduleKeys = variant === 'simple' ? MATRIX_MODULE_KEYS : activeModuleKeys;
  const moduleLabels = variant === 'simple' ? MATRIX_MODULE_LABELS : MASTER_MODULE_LABELS;

  if (moduleKeys.length === 0) {
    return (
      <Alert severity="info">
        No modules are active yet. Ask your platform administrator to enable modules before
        configuring role permissions.
      </Alert>
    );
  }

  if (editableRoles.length === 0) {
    return (
      <Alert severity="info">
        Your role cannot edit granular permissions. Assign users a role instead — permissions
        inherit from that role&apos;s template.
      </Alert>
    );
  }

  const isModuleActive = (moduleKey) =>
    variant !== 'simple' || activeModuleKeys.includes(moduleKey);

  const handleDetailedToggle = (role, module, action, checked) => {
    onChange(toggleRolePermission(rolePermissions, role, module, action, checked));
  };

  const handleSimpleToggle = (role, module, checked) => {
    onChange(toggleRoleModuleEnabled(rolePermissions, role, module, checked));
  };

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Role</TableCell>
            {moduleKeys.map((moduleKey) => (
              <TableCell key={moduleKey} align="center" sx={{ fontWeight: 700, minWidth: 120 }}>
                {moduleLabels[moduleKey] || MASTER_MODULE_LABELS[moduleKey] || moduleKey}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {editableRoles.map((role) => (
            <TableRow key={role}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {PERMISSION_ROLE_LABELS[role] || role}
                </Typography>
              </TableCell>
              {moduleKeys.map((moduleKey) => {
                const moduleEnabled = isModuleActive(moduleKey);
                if (variant === 'simple') {
                  return (
                    <TableCell key={`${role}-${moduleKey}`} align="center">
                      <Switch
                        size="small"
                        disabled={disabled || !moduleEnabled}
                        checked={isRoleModuleEnabled(rolePermissions, role, moduleKey)}
                        onChange={(e) => handleSimpleToggle(role, moduleKey, e.target.checked)}
                        inputProps={{
                          'aria-label': `${PERMISSION_ROLE_LABELS[role] || role} ${MATRIX_MODULE_LABELS[moduleKey] || moduleKey}`,
                        }}
                      />
                    </TableCell>
                  );
                }

                return (
                  <TableCell key={`${role}-${moduleKey}`} align="center">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <FormControlLabel
                        control={(
                          <Checkbox
                            size="small"
                            disabled={disabled || !moduleEnabled}
                            checked={hasRolePermission(rolePermissions, role, moduleKey, 'read')}
                            onChange={(e) =>
                              handleDetailedToggle(role, moduleKey, 'read', e.target.checked)
                            }
                          />
                        )}
                        label="View"
                        sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                      />
                      <FormControlLabel
                        control={(
                          <Checkbox
                            size="small"
                            disabled={disabled || !moduleEnabled}
                            checked={hasRolePermission(rolePermissions, role, moduleKey, 'write')}
                            onChange={(e) =>
                              handleDetailedToggle(role, moduleKey, 'write', e.target.checked)
                            }
                          />
                        )}
                        label="Manage"
                        sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                      />
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {variant === 'simple' && activeModuleKeys.length < MATRIX_MODULE_KEYS.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, px: 1 }}>
          Switches for modules not enabled on this workspace are disabled. Enable them under Settings → Workspace.
        </Typography>
      )}
    </TableContainer>
  );
}
